import { useEffect, useRef } from 'react';
import {
  IntersectionOptions,
  type InViewHookResponse,
  useInView,
} from 'react-intersection-observer';

export type UseInViewOnceOptions = IntersectionOptions;

export type UseInViewOnceReturn = Pick<InViewHookResponse, 'ref' | 'inView'>;

/**
 * Triggers a callback function only once when an element enters the viewport.
 * Automatically disconnects the IntersectionObserver after the first trigger.
 *
 * @param callback - Optional function to call when the element is first viewed. Pass undefined to disable the callback.
 * @param options - IntersectionObserver configuration options
 * @returns Object with `ref` to attach to the element and `inView` boolean
 */
export const useInViewOnce = (
  callback?: () => void,
  options: UseInViewOnceOptions = {},
): UseInViewOnceReturn => {
  const hasTriggered = useRef(false);

  const { ref, inView: isInView } = useInView({
    triggerOnce: true,
    ...options,
  });

  useEffect(() => {
    if (isInView && !hasTriggered.current && callback) {
      hasTriggered.current = true;
      callback();
    }
  }, [isInView, callback]);

  return { ref, inView: isInView } as UseInViewOnceReturn;
};
